import React, { lazy, Suspense, useCallback, useMemo, useState } from 'react';

import { EOrderState, EOrderAsLastTimeState } from '@model';
import { useTrackCheckoutCloseRepeatedPurchase, EcommerceSectionNamespace } from '@services/ecommerceService';
import { EPaypalComponentState, PAYPAL_COMPONENT_LOADER_HEIGHT, usePaypalApi } from '@services/paymentService';
import { PaymentError } from '@services/paymentService';
import { useRedirect } from '@services/routingService';
import { EPage } from '@types';

import { useCartPageContext } from '../../../../context';
import { createEcommerceOaltCheckout } from '../../../../ecommerce';
import { IOaLTProcessProps, TOaLTProcessOutput } from '../../model';
import { generalMessages } from '../generalMessages';
import { useCheckoutInitOrder } from '../useCheckoutInitOrder';

import { paypalMessages } from './paypalMessages';
import { SLoader } from './styled';

const PaypalComponent = lazy(() => import('@services/paymentService/paypal'));

export const usePaypalProcessForOalt = ({
  setState,
  lastOrder,
  onClose,
  setLoading,
}: IOaLTProcessProps & {
  onClose: () => void;
  setLoading: (isLoading: boolean) => void;
}): TOaLTProcessOutput => {
  const {
    state: { pricesSummary, products, serviceItems },
    settings: { paypal },
  } = useCartPageContext();

  const [componentState, setComponentState] = useState(EPaypalComponentState.PAYMENT);

  const redirect = useRedirect();
  const trackCloseOalt = useTrackCheckoutCloseRepeatedPurchase();

  const { request: checkoutInitOrder } = useCheckoutInitOrder();

  const useCheckoutInit = useCallback(async () => {
    if (lastOrder) {
      const result = await checkoutInitOrder(lastOrder);
      return result;
    }

    return null;
  }, [lastOrder, checkoutInitOrder]);

  const trackClose = useCallback(() => {
    if (lastOrder) {
      trackCloseOalt(
        createEcommerceOaltCheckout(lastOrder, products, pricesSummary, serviceItems),
        EOrderAsLastTimeState.ERROR,
      );
    }
  }, [lastOrder, pricesSummary, products, serviceItems, trackCloseOalt]);

  const onRepeatButton = useCallback(() => {
    trackClose();
    setState(EOrderAsLastTimeState.INFO_PAGE);
    setComponentState(EPaypalComponentState.PAYMENT);
    setLoading(false);
  }, [trackClose, setState, setLoading]);

  const onCloseButton = useCallback(() => {
    onClose();
    onRepeatButton();
  }, [onClose, onRepeatButton]);

  const onSuccess = useCallback(() => {
    onClose();
    redirect(EPage.THANK_YOU);
  }, [onClose, redirect]);

  const { onInit, onProcess } = usePaypalApi({ setComponentState, setPageState: setState, onSuccess });

  return useMemo(() => {
    return {
      actionComponent: lastOrder && paypal.clientId && (
        <Suspense fallback={<SLoader height={PAYPAL_COMPONENT_LOADER_HEIGHT} />}>
          {componentState === EPaypalComponentState.PAYMENT && (
            <PaypalComponent
              onSuccess={onSuccess}
              setPageState={setState}
              clientId={paypal.clientId}
              setComponentState={setComponentState}
              checkoutInitOrder={useCheckoutInit}
            />
          )}
          {componentState === EPaypalComponentState.ERROR && (
            <PaymentError
              messages={{ ...paypalMessages, ...generalMessages }}
              onRepeatPayment={onRepeatButton}
              onReturnToPreviousStep={onCloseButton}
              amount={lastOrder.orderPricesSummary?.totalPrice ?? 0}
              provider={EcommerceSectionNamespace.EEcommercePaymentProvider.PAYPAL}
              providerType={EcommerceSectionNamespace.EEcommercePaymentProvider.PAYPAL}
              titleMessage={paypalMessages.paymentErrorMessage}
            />
          )}
        </Suspense>
      ),
      onContinueToCheckout: async () => {
        if (!lastOrder) {
          return;
        }

        setLoading(true);
        const result = await checkoutInitOrder(lastOrder);

        if (!result) {
          return;
        }

        if (result.orderState === EOrderState.Complete) {
          onSuccess();
          return;
        }

        const responseInit = await onInit();
        if (!responseInit) {
          return;
        }

        if (lastOrder.paymentInfo.paypalTokenAvailable && responseInit.orderId) {
          onSuccess();
          return;
        }

        await onProcess(responseInit.paypalOrderId);
      },
    };
  }, [
    lastOrder,
    paypal.clientId,
    componentState,
    onSuccess,
    setState,
    useCheckoutInit,
    onRepeatButton,
    onCloseButton,
    setLoading,
    checkoutInitOrder,
    onInit,
    onProcess,
  ]);
};
